import { Link } from 'react-router-dom';
import { useAuth } from '../../../auth/AuthProvider';
import { usePlanGate } from '../../../hooks/usePlanGate';
import { cx } from 'class-variance-authority';

const planBadgeColor: Record<string, string> = {
  free: 'bg-neutral-100 text-neutral-600',
  pro: 'bg-blue-100 text-blue-700',
  elite: 'bg-purple-100 text-purple-700',
  enterprise: 'bg-amber-100 text-amber-700',
};

const roleBadgeColor: Record<string, string> = {
  viewer: 'bg-neutral-100 text-neutral-600',
  editor: 'bg-blue-100 text-blue-700',
  approver: 'bg-purple-100 text-purple-700',
  admin: 'bg-amber-100 text-amber-700',
};

export const AccountSection = () => {
  const { user } = useAuth();
  const { planInfo } = usePlanGate();

  if (!user) {
    return (
      <div className="flex items-center justify-center h-32 text-neutral-400 text-sm">
        Loading...
      </div>
    );
  }

  const userEmail = user.email;
  const workspaceRole = user.role || 'viewer';
  const orgRole = (user as Record<string, unknown> | null)?.org_role as string | undefined;
  const orgId = (user as Record<string, unknown> | null)?.org_id as string | undefined;
  const plan = planInfo?.plan || 'free';

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-neutral-900">Account</h1>
          <p className="text-sm text-neutral-600">
            View your account information and manage your profile settings.
          </p>
        </div>

        {/* Account Information Card */}
        <section className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6 space-y-6">
          <header>
            <h2 className="text-lg font-semibold text-neutral-900">Account Information</h2>
            <p className="text-sm text-neutral-600 mt-1">
              Your account details and role information.
            </p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-1">
              <label className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                Email Address
              </label>
              <p className="text-sm font-medium text-neutral-900">{userEmail}</p>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                Workspace Role
              </label>
              <div>
                <span
                  className={cx(
                    'inline-block px-2 py-1 rounded text-xs font-medium uppercase tracking-wide',
                    roleBadgeColor[workspaceRole] || roleBadgeColor.viewer,
                  )}
                >
                  {workspaceRole}
                </span>
              </div>
            </div>

            {orgRole && (
              <div className="space-y-1">
                <label className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                  Organization Role
                </label>
                <div>
                  <span
                    className={cx(
                      'inline-block px-2 py-1 rounded text-xs font-medium uppercase tracking-wide',
                      orgRole === 'admin'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-neutral-100 text-neutral-600',
                    )}
                  >
                    {orgRole}
                  </span>
                </div>
              </div>
            )}

            {orgId && (
              <div className="space-y-1">
                <label className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                  Organization ID
                </label>
                <p className="text-sm font-mono text-neutral-600 break-all">{orgId}</p>
              </div>
            )}
          </div>
        </section>

        {/* Plan Information Card */}
        {planInfo && (
          <section className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6 space-y-6">
            <header>
              <h2 className="text-lg font-semibold text-neutral-900">Plan Information</h2>
              <p className="text-sm text-neutral-600 mt-1">
                Your current plan and available features.
              </p>
            </header>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-600">Current Plan</span>
                <span
                  className={cx(
                    'inline-block px-3 py-1 rounded text-sm font-medium uppercase tracking-wide',
                    planBadgeColor[plan] || planBadgeColor.free,
                  )}
                >
                  {plan}
                </span>
              </div>

              {planInfo.max_members !== null && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-neutral-600">Max Members</span>
                  <span className="text-sm font-medium text-neutral-900">
                    {planInfo.max_members > 999999
                      ? 'Unlimited'
                      : planInfo.max_members.toLocaleString()}
                  </span>
                </div>
              )}

              {planInfo.max_projects !== null && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-neutral-600">Max Projects</span>
                  <span className="text-sm font-medium text-neutral-900">
                    {planInfo.max_projects > 999999
                      ? 'Unlimited'
                      : planInfo.max_projects.toLocaleString()}
                  </span>
                </div>
              )}

              {planInfo.monthly_token_limit !== null && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-neutral-600">Monthly Token Limit</span>
                  <span className="text-sm font-medium text-neutral-900">
                    {planInfo.monthly_token_limit > 999999999
                      ? 'Unlimited'
                      : planInfo.monthly_token_limit.toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Profile Link Card */}
        <section className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-neutral-900">Profile Settings</h2>
              <p className="text-sm text-neutral-600 mt-1">
                Manage your individual and organizational context to help the AI better understand
                your work.
              </p>
            </div>
            <Link
              to="/profile"
              className="inline-flex items-center rounded-md bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-500"
            >
              Edit Profile
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};
